import React, { useState } from "react";
import ThemeProvider from "@onnit-js/ui/components/themes/ThemeProvider";
import GlobalStyle from "@onnit-js/ui/components/themes/GlobalStyle";
import theme from "@onnit-js/ui/components/themes/light";
import Box from "@onnit-js/ui/components/box/Box";
import { List, ListItem, Sup, Text } from "@onnit-js/ui/components/text";
import useOnnitContext from "@onnit-js/ui/hooks/useOnnitContext";
import { Img, Picture } from "@onnit-js/ui/components/image";
import ProductImageGrid from "@onnit-js/ui/components/module/ProductImageGrid";
import TwelveColumn from "@onnit-js/ui/components/module/grid/12/Grid";
import MediaContentShowcase from "@onnit-js/ui/components/module/MediaContentShowcase";
import usePageData from "../../hooks/usePageData";
import SupplementFacts from "../../components/common/SupplementFacts";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import config from "../../config/config";
import ingredients from "./ingredients";

const transformProductName = (name: string): string => name.replace(/Vitamin D3 spray with Vitamin K2 in MCT Oil - /giu, "");

export default function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();
    // Used to set current products supplement facts image.
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) {
        return null;
    }

    const IMG_URL = `${onnitContext.url.cdn.static_images}/product-page/krill-oil`;
    const { IMG_PARAMS } = config;
    const mainColor = "#64D1E2";

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />

                <DefaultProductPageTop
                    productNameTransform={transformProductName}
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid>
                        <Picture
                            lazyLoad={false}
                            width={1943}
                            height={873}
                            alt="Bottle of Krill Oil floating under Alaskan sea"
                            src={[
                                `${IMG_URL}/2024-KRILL-Wpage-Gallery-1-920x920.png${IMG_PARAMS}`,
                                `${IMG_URL}/2024-KRILL-Wpage-Gallery-1-1940x873.png${IMG_PARAMS}`,
                            ]}
                        />
                        <Img
                            alt="detailed view of a single krill"
                            src={`${IMG_URL}/2024-KRILL-Wpage-Gallery-2-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                        />
                        <Img
                            alt="school of krill in the ocean"
                            src={`${IMG_URL}/2024-KRILL-Wpage-Gallery-3-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <Box bg="#161616" mt={[0, 0, 6, 8]} py={[6, 6, 6, 8]}>
                    <TwelveColumn display={["none", "none", "grid"]} mb={4}>
                        <Box gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 7"]} px={4}>
                            <Text as="h2" color="grays.3" typeStyle="text02" fontWeight="regular">
                                Krill Oil
                            </Text>
                        </Box>
                    </TwelveColumn>
                    <TwelveColumn display={["block", "block", "grid"]} mb={[0, 0, 6]}>
                        <Box
                            gridColumn={["7 / span 6", "7 / span 6", "7 / span 6", "7 / span 5"]}
                            px={4}
                            mb={[6, 6, 0]}
                        >
                            <Img
                                src={`${IMG_URL}/2024-KRILL-Wpage-Product-Hero-1920x1080.png${IMG_PARAMS}`}
                                alt="detailed image of a krill"
                            />
                        </Box>
                        <Box
                            gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 5"]}
                            display={["block", "block", "flex"]}
                            alignItems="center"
                            px={4}
                        >
                            <Text
                                display={["block", "block", "none"]}
                                as="h2"
                                color="grays.3"
                                typeStyle="text02"
                                fontWeight="regular"
                                my={3}
                            >
                                Krill Oil
                            </Text>

                            <Text as="h3" typeStyle="title01" color={mainColor} mb={2}>
                                Go For The Krill
                            </Text>
                        </Box>
                    </TwelveColumn>

                    <TwelveColumn display={["block", "block", "grid"]}>
                        <Box
                            gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 7"]}
                            px={4}
                            mb={[6, 6, 6, 8]}
                        >
                            <Text as="p" color="white" typeStyle="text02" mb={4} fontWeight="regular">
                                Omega-3 fats are widely known to support multiple aspects of health.
                                <Sup variant="dagger" />{" "}Your body can’t make them, so you must get them from food
                                or supplements. Krill Oil provides omega-3s, just as fish oil does, but also nutrients
                                that fish oil can't match, such as phospholipids and astaxanthin.
                            </Text>
                            <Text as="p" color="white" typeStyle="text02" mb={4} fontWeight="regular">
                                Furthermore, less than 1% of the Antarctic krill population is harvested for human
                                consumption. The omega-3s in Krill Oil have earned a certification from the Marine
                                Stewardship Council, an ocean conservation group.
                            </Text>
                            <Text as="p" color="white" typeStyle="text02" mb={4} fontWeight="regular">
                                Finally, Onnit's Krill Oil comes in a small soft gel, which is easier to swallow than
                                most large fish oil capsules and less likely to come back up in a "fish burp."
                            </Text>
                        </Box>
                        <Box gridColumn={["2 / span 10", "2 / span 10", "2 / span 10", "3 / span 10"]} px={4}>
                            <Text as="h3" typeStyle="title01" color={mainColor} mb={4}>
                                Krill Oil is for:
                            </Text>
                            <List>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular" mb={3}>
                                    Those who value longevity
                                </ListItem>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular" mb={3}>
                                    People who live an active lifestyle
                                </ListItem>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular">
                                    Athletes and gym goers
                                </ListItem>
                            </List>
                        </Box>
                    </TwelveColumn>
                </Box>

                <Box bg="#161616" py={[6, 6, 6, 8]}>
                    <TwelveColumn display={["block", "block", "block", "grid"]} px={5} mb={6}>
                        <Text
                            as="h3"
                            typeStyle="copyDisplay02"
                            color="white"
                            gridColumn={["1 / -1", "1 / -1", "2 / span 12"]}
                        >
                            Key Ingredients
                        </Text>
                    </TwelveColumn>
                    <MediaContentShowcase
                        showImages={false}
                        items={ingredients.map((item) => {
                            const copy = { ...item };
                            copy.image = `${IMG_URL}/${copy.image}`;
                            return copy;
                        })}
                        activeColor={mainColor}
                        textColor="white"
                        bgMedia={(
                            <Img
                                alt="ocean with ice"
                                src={`${IMG_URL}/2024-KRILL-Wpage-Ingredient-Background.png${IMG_PARAMS}`}
                                width={1120}
                                height={315}
                            />
                          )}
                    />
                </Box>

                <UsageAndDisclaimers supplementWarning supplementFacts={<SupplementFacts currentPID={currentPID} />} />
            </>
        </ThemeProvider>
    );
}
